import { Typography } from "@mui/material";
import { GridRowSelectionModel, GridSortItem } from "@mui/x-data-grid";
import { CHART_TYPOGRAPHY } from "../../../../../components/app/provider/patient/chart";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import Flex from "../../../../../components/Flex";
import StatusChip from "../../../../../components/StatusChip";
import { formatDate } from "../../../../../utils/date-time-utility";
import {
  DGT_Pagination,
  getTrimmedUuid,
  getValueFromObj,
} from "../../../../../utils/utils";
import {
  BILLING_STATUS_MAP,
  getBillingColumns,
} from "./billing-report-constants";

interface BillingReportTableProps {
  rows: any[];
  rowCount: number;
  loading: boolean;
  pagination: DGT_Pagination;
  onPaginationChange: (model: DGT_Pagination) => void;
  gridSorting: GridSortItem[];
  onSortChange: (model: GridSortItem[]) => void;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
}

const renderClaimId = ({ row }: any) => (
  <Typography
    fontSize={CHART_TYPOGRAPHY.body5.fontSize}
    fontWeight={500}
    // color="#004AB1"
    sx={{
      cursor: "pointer",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {getTrimmedUuid(row?.uuid) ?? ""}
  </Typography>
);

const renderCptCode = ({ row }: any) => {
  const firstCode = getValueFromObj(row?.cptCodes);
  if (!firstCode) return <Typography fontSize={14}>-</Typography>;

  const remaining = Object.keys(row?.cptCodes ?? {}).length - 1;

  return (
    <Flex gap="4px" alignItems="center" flexWrap="wrap">
      <StatusChip
        label={firstCode}
        bgColor={"#F2F4F7"}
        color={"#373D41"}
        sx={{
          maxWidth: "150px",
          "& .MuiChip-label": {
            textAlign: "left",
          },
        }}
      />
      {remaining > 0 && (
        <StatusChip
          label={`+${remaining}`}
          bgColor={"#F2F4F7"}
          color={"#373D41"}
        />
      )}
    </Flex>
  );
};

const renderStatusCell = ({ row }: any) => {
  if (!row?.claimStatus) return <></>;
  const statusConfig = BILLING_STATUS_MAP[row?.claimStatus] ?? {
    label: row?.claimStatus ?? "",
    color: "#999",
    bgColor: "#F5F5F5",
  };
  return (
    <StatusChip
      label={statusConfig.label}
      color={statusConfig.color}
      bgColor={statusConfig.bgColor}
    />
  );
};

const COLUMNS = getBillingColumns(
  renderClaimId,
  renderCptCode,
  renderStatusCell
);

const BillingReportTable = ({
  rows,
  rowCount,
  loading,
  pagination,
  onPaginationChange,
  gridSorting,
  onSortChange,
  rowSelectionModel,
  onRowSelectionModelChange,
}: BillingReportTableProps) => {
  const formattedRows = (rows ?? []).map((row: any) => ({
    ...row,
    id: row?.uuid ?? row?.claimId,
    dateOfService: formatDate(row?.dateOfService) ?? "-",
    billed: row?.billed != null ? `$${Number(row.billed).toFixed(2)}` : "-",
    paid: row?.paid != null ? `$${Number(row.paid).toFixed(2)}` : "-",
    days: row?.days != null ? `${row.days} days` : "-",
    insuranceName: row?.insuranceName ?? "-",
  }));

  return (
    <>
      <CustomDataGridV2
        rows={formattedRows}
        getRowId={(row) => row?.uuid ?? row?.id}
        columns={COLUMNS}
        rowCount={rowCount ?? 0}
        loading={loading}
        paginationModel={pagination}
        onPaginationModelChange={onPaginationChange}
        sortModel={gridSorting}
        onSortModelChange={onSortChange}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
      />
    </>
  );
};

export default BillingReportTable;
